import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import ToolResultCard from "@/components/tools/ToolResultCard"
import { NumberField, SelectField, SubmitButton, ResetButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FormulaBox, ExampleBox, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-emv-calculator")!

const NICHES = [
  { value: "general", label: "General Lifestyle", cpm: 6 },
  { value: "fashion", label: "Fashion & Beauty", cpm: 8 },
  { value: "fitness", label: "Fitness & Health", cpm: 7 },
  { value: "tech", label: "Tech & Gaming", cpm: 10 },
  { value: "finance", label: "Finance & Business", cpm: 12 },
  { value: "food", label: "Food & Cooking", cpm: 6 },
  { value: "travel", label: "Travel & Adventure", cpm: 7 },
]

const CONTENT_TYPES = [
  { value: "post", label: "Feed Post", multiplier: 1.0 },
  { value: "reel", label: "Reel", multiplier: 1.3 },
  { value: "story", label: "Story", multiplier: 0.7 },
  { value: "carousel", label: "Carousel", multiplier: 1.15 },
]

export const Route = createFileRoute("/tools/instagram-emv-calculator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({ title: tool.seo.title, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-emv-calculator" }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-emv-calculator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-emv-calculator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-emv-calculator" },
      ]}),
      createHowToSchema({ name: "How to Calculate Instagram Earned Media Value", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: EMVCalculatorPage,
})

function EMVCalculatorPage() {
  const [impressions, setImpressions] = useState("")
  const [niche, setNiche] = useState("general")
  const [contentType, setContentType] = useState("post")
  const [result, setResult] = useState<{ emv: number; cpm: number; influencerValue: number } | null>(null)

  const calculate = () => {
    const imp = Number(impressions)
    if (!imp || imp <= 0) return
    const nicheData = NICHES.find((n) => n.value === niche)!
    const contentData = CONTENT_TYPES.find((c) => c.value === contentType)!
    const adjustedCPM = nicheData.cpm * contentData.multiplier
    const emv = (imp * adjustedCPM) / 1000
    const influencerValue = emv * 2 // typical 1.5-3x
    setResult({ emv, cpm: adjustedCPM, influencerValue })
  }

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${Math.round(n)}`

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>What is Earned Media Value (EMV)?</SectionHeading>
            <Paragraph>
              Earned Media Value (EMV) is a metric that assigns a dollar amount to organic social media exposure by estimating what the same reach would cost through paid advertising. If a brand had to buy the impressions your post generated through Instagram Ads, how much would they spend? That is your EMV. It bridges the gap between the intangible value of creator content and the concrete budgets brands work with.
            </Paragraph>
            <Paragraph className="mt-3">
              For creators, EMV is a powerful negotiation tool. Instead of saying "my post got 50,000 views," you can say "my post delivered $350 in earned media value, which means the brand deal I am proposing gives you a 3x return on your investment." This reframes the conversation from vanity metrics to business value and puts you in a much stronger position when discussing rates.
            </Paragraph>
            <Paragraph className="mt-3">
              Brands and agencies use EMV to measure the effectiveness of influencer campaigns, compare ROI across different creators, and justify marketing spend to stakeholders. Understanding how EMV works and how to calculate it gives you a significant advantage in the creator economy.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>EMV Formula Explained</SectionHeading>
            <Paragraph>
              The standard EMV formula is straightforward. It multiplies your impressions by the cost-per-mille (CPM), which is the cost per 1,000 ad impressions in your niche, then divides by 1,000 to normalize the result.
            </Paragraph>
            <FormulaBox>
              EMV = (Impressions x CPM) / 1,000
            </FormulaBox>
            <Paragraph className="mt-4">
              Here are three worked examples at different impression levels to illustrate how EMV scales:
            </Paragraph>
            <ExampleBox title="Small post (5,000 impressions, $7 CPM)">
              EMV = (5,000 x $7) / 1,000 = <strong>$35</strong>. Typical of a nano influencer feed post in a general lifestyle niche. Even at this scale, knowing your EMV helps you justify rates above the $10-$25 range many brands initially offer nano creators.
            </ExampleBox>
            <ExampleBox title="Mid-range Reel (50,000 impressions, $8 CPM, 1.3x Reel multiplier)">
              EMV = (50,000 x $10.40) / 1,000 = <strong>$520</strong>. A micro influencer Reel in the fashion niche. The Reel multiplier increases the effective CPM because video content drives higher attention and recall than static posts.
            </ExampleBox>
            <ExampleBox title="Viral post (500,000 impressions, $12 CPM)">
              EMV = (500,000 x $12) / 1,000 = <strong>$6,000</strong>. A finance niche creator who hits the Explore page. At this level, the EMV alone demonstrates significant media value, and the actual rate you should charge is 2x to 3x this number.
            </ExampleBox>
          </ContentSection>

          <ContentSection>
            <SectionHeading>EMV by Content Type</SectionHeading>
            <Paragraph>
              Not all content formats deliver equal media value. The multiplier applied to your base CPM varies by format because each type generates different levels of audience attention, recall, and action. Here is how the main Instagram content types compare.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { icon: "\uD83D\uDCF7", title: "Feed Posts (1.0x baseline)", description: "Standard single-image posts serve as the baseline for EMV calculations. They are the most common sponsored content format and have predictable performance. CPM values for feed posts are well-established across niches, making them the anchor for all other multipliers." },
              { icon: "\uD83C\uDFAC", title: "Reels (1.3x multiplier)", description: "Reels command a 30% premium because short-form video content drives significantly higher engagement, watch time, and algorithmic reach than static posts. Instagram actively promotes Reels in the Explore page and Reels tab, giving them bonus distribution." },
              { icon: "\u23F3", title: "Stories (0.7x multiplier)", description: "Stories carry a lower multiplier because they disappear after 24 hours and reach a smaller percentage of your followers compared to feed content. However, they excel at driving direct actions through link stickers, polls, and question boxes." },
              { icon: "\uD83D\uDCDA", title: "Carousels (1.15x multiplier)", description: "Carousel posts earn a 15% premium because they generate higher engagement rates than single-image posts. Users who swipe through multiple frames spend more time with the content, and Instagram's algorithm rewards that extended engagement with broader distribution." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>EMV vs. Actual Influencer Rates</SectionHeading>
            <Paragraph>
              A common question creators have is "should I charge my EMV as my rate?" The answer is no. Actual influencer rates are typically 1.5x to 3x the calculated EMV, and there are good reasons for this markup.
            </Paragraph>
            <Paragraph className="mt-3">
              EMV only accounts for the raw media exposure, the equivalent of buying ad impressions. But when a brand works with you, they are getting much more than impressions. They receive your creative expertise, your authentic voice, the trust your audience places in your recommendations, and content they can often repurpose across their own channels. A paid ad impression from Instagram is a cold, interruptive format. An impression from your organic post carries the weight of a personal recommendation, which is far more persuasive and valuable.
            </Paragraph>
            <Paragraph className="mt-3">
              As a general rule, nano and micro influencers should charge around 1.5x to 2x their EMV because they are still building their portfolio and negotiation leverage. Mid-tier and macro creators can confidently charge 2x to 3x because they bring proven track records, professional production quality, and larger audiences that are difficult to reach through ads alone. If your content consistently drives sales or sign-ups, you can justify rates even above 3x EMV by sharing conversion data from past campaigns.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>How to Use EMV to Negotiate Brand Deals</SectionHeading>
            <Paragraph>
              EMV is most powerful when you use it as a conversation tool in brand negotiations. Here are four practical ways to leverage your EMV data.
            </Paragraph>
            <TipList tips={[
              { title: "Include EMV in your media kit", description: "Add a \"Past Campaign EMV\" section to your media kit showing the EMV you generated for previous brand partners. This gives potential clients a clear picture of the media value they can expect and positions you as a data-driven professional rather than someone guessing at rates." },
              { title: "Frame your rate as ROI, not cost", description: "Instead of saying \"my rate is $1,000,\" say \"based on my average reach and a niche CPM of $10, each post I create delivers approximately $600 in earned media value, meaning your effective cost after accounting for organic reach is only $400.\" This reframes the expense as an investment with measurable returns." },
              { title: "Use EMV to justify rate increases", description: "If your engagement and reach have grown since your last campaign, calculate the EMV difference and present it as the reason for your higher rate. \"My average Reel now reaches 80K versus 40K last quarter, so my EMV per deliverable has doubled\" is a hard argument for a brand to counter." },
              { title: "Compare EMV to the brand's ad spend", description: "Research what the brand is spending on Instagram ads using tools like the Meta Ad Library. If their cost per 1,000 impressions through ads is $15 and your organic EMV delivers the same reach at a $7 effective CPM, you have a compelling value proposition that makes your collaboration look like a bargain compared to their paid media budget." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!result ? (
            <div className="space-y-4">
              <NumberField label="Impressions / Reach" value={impressions} onChange={setImpressions} placeholder="e.g. 10000" required min={1} />
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  label="Content Niche"
                  value={niche}
                  onChange={setNiche}
                  options={NICHES.map(n => ({ value: n.value, label: n.label }))}
                />
                <SelectField
                  label="Content Type"
                  value={contentType}
                  onChange={setContentType}
                  options={CONTENT_TYPES.map(c => ({ value: c.value, label: c.label }))}
                />
              </div>
              <SubmitButton onClick={calculate} disabled={!impressions}>
                Calculate EMV
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Your Earned Media Value</p>
                <p className="text-5xl sm:text-6xl font-bold text-brand-button">{fmt(result.emv)}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <ToolResultCard label="EMV" value={fmt(result.emv)} variant="primary" />
                <ToolResultCard label="CPM Used" value={`$${result.cpm.toFixed(1)}`} />
                <ToolResultCard label="Influencer Value (2x)" value={fmt(result.influencerValue)} variant="success" sublabel="Typical rate to charge" />
              </div>
              <ResetButton onClick={() => setResult(null)} />
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
